import React from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';
import { Colors, Spacing, FontSizes, BorderRadius, Shadows } from '../constants/theme';

const ChartContainer = styled.div`
  background: ${Colors.surface};
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.md}px;
  padding: ${Spacing.xl}px;
  box-shadow: ${Shadows.sm};
  margin-bottom: ${Spacing.xl}px;
`;

const ChartTitle = styled.h3`
  font-size: ${FontSizes.lg}px;
  color: ${Colors.textPrimary};
  margin-bottom: ${Spacing.lg}px;
`;

interface ChartProps {
  title: string;
  data: any[];
  type: 'line' | 'bar' | 'pie' | 'area' | 'radar';
  dataKey?: string;
  xDataKey?: string;
  height?: number;
}

const CHART_COLORS = [
  Colors.primary,
  Colors.secondary,
  Colors.accent,
  Colors.success,
  Colors.warning,
  Colors.danger,
  Colors.info
];

export const DataChart: React.FC<ChartProps> = ({
  title,
  data,
  type,
  dataKey = 'value',
  xDataKey = 'name',
  height = 300
}) => {
  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={Colors.border} />
              <XAxis dataKey={xDataKey} stroke={Colors.textTertiary} />
              <YAxis stroke={Colors.textTertiary} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: Colors.surface, 
                  border: `1px solid ${Colors.border}`,
                  borderRadius: BorderRadius.sm
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey={dataKey} 
                stroke={Colors.primary} 
                strokeWidth={2}
                dot={{ fill: Colors.primary, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={Colors.border} />
              <XAxis dataKey={xDataKey} stroke={Colors.textTertiary} />
              <YAxis stroke={Colors.textTertiary} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: Colors.surface, 
                  border: `1px solid ${Colors.border}`,
                  borderRadius: BorderRadius.sm
                }}
              />
              <Legend />
              <Bar dataKey={dataKey} fill={Colors.primary} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'pie':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey={dataKey}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: Colors.surface, 
                  border: `1px solid ${Colors.border}`,
                  borderRadius: BorderRadius.sm
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={Colors.border} />
              <XAxis dataKey={xDataKey} stroke={Colors.textTertiary} />
              <YAxis stroke={Colors.textTertiary} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: Colors.surface, 
                  border: `1px solid ${Colors.border}`,
                  borderRadius: BorderRadius.sm
                }}
              />
              <Legend />
              <Area 
                type="monotone" 
                dataKey={dataKey} 
                stroke={Colors.primary} 
                fill={Colors.primary}
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      
      case 'radar':
        return (
          <ResponsiveContainer width="100%" height={height}>
            <RadarChart data={data}>
              <PolarGrid stroke={Colors.border} />
              <PolarAngleAxis dataKey={xDataKey} stroke={Colors.textTertiary} />
              <PolarRadiusAxis stroke={Colors.textTertiary} />
              <Radar 
                name="Score" 
                dataKey={dataKey} 
                stroke={Colors.primary} 
                fill={Colors.primary} 
                fillOpacity={0.6}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: Colors.surface, 
                  border: `1px solid ${Colors.border}`,
                  borderRadius: BorderRadius.sm
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  return (
    <ChartContainer>
      <ChartTitle>{title}</ChartTitle>
      {renderChart()}
    </ChartContainer>
  );
};

interface PerformanceChartProps {
  data: Array<{
    month: string;
    performance: number;
    engagement: number;
    attrition: number;
  }>;
}

export const PerformanceChart: React.FC<PerformanceChartProps> = ({ data }) => {
  return (
    <ChartContainer>
      <ChartTitle>Performance Trends</ChartTitle>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke={Colors.border} />
          <XAxis dataKey="month" stroke={Colors.textTertiary} />
          <YAxis stroke={Colors.textTertiary} />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: Colors.surface, 
              border: `1px solid ${Colors.border}`,
              borderRadius: BorderRadius.sm
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="performance" 
            stroke={Colors.success} 
            strokeWidth={2}
            name="Performance"
          />
          <Line 
            type="monotone" 
            dataKey="engagement" 
            stroke={Colors.primary} 
            strokeWidth={2}
            name="Engagement"
          />
          <Line 
            type="monotone" 
            dataKey="attrition" 
            stroke={Colors.danger} 
            strokeWidth={2}
            name="Attrition Risk"
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

interface DepartmentDistributionProps {
  data: Array<{
    name: string;
    value: number;
  }>;
}

export const DepartmentDistribution: React.FC<DepartmentDistributionProps> = ({ data }) => {
  return (
    <ChartContainer>
      <ChartTitle>Department Distribution</ChartTitle>
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
            outerRadius={100}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip 
            contentStyle={{ 
              backgroundColor: Colors.surface, 
              border: `1px solid ${Colors.border}`,
              borderRadius: BorderRadius.sm
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

interface SkillsRadarProps {
  data: Array<{
    skill: string;
    score: number;
  }>;
}

export const SkillsRadar: React.FC<SkillsRadarProps> = ({ data }) => {
  return (
    <ChartContainer>
      <ChartTitle>Skills Assessment</ChartTitle>
      <ResponsiveContainer width="100%" height={350}>
        <RadarChart data={data}>
          <PolarGrid stroke={Colors.border} />
          <PolarAngleAxis dataKey="skill" stroke={Colors.textTertiary} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} stroke={Colors.textTertiary} />
          <Radar 
            name="Current Level" 
            dataKey="score" 
            stroke={Colors.primary} 
            fill={Colors.primary} 
            fillOpacity={0.6}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: Colors.surface, 
              border: `1px solid ${Colors.border}`,
              borderRadius: BorderRadius.sm
            }}
          />
          <Legend />
        </RadarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};